import React, { useEffect, useState, useRef } from "react";
import data from "../../data/data.ts";
import "../../components/profile/profile.css";
import DisplayCard from "../../common/displayCard";
import {
  getProfileList,
  isMobile,
  setProfileList,
  setTooltipStatus,
  removeTooltipStatus,
  useKeyBoardShortcut,
  usePageStructureSync,
  useVoiceNavSync,
} from "../../store/user";
import { useDispatch, useSelector } from "react-redux";
import OuterRangeSlider from "../../common/outerRangerSlider";
import { selectIsResetOpen } from "../../reduxSlices/resetToggleSlice";
import { setProfileData as setProfileDataRedux } from "../../reduxSlices/profile";
import {
  setDictionaryActive,
  setPageStructureActive,
  setVoiceNavigationActive,
} from "../../reduxSlices/booleanSlice";
import { useTranslation } from "react-i18next";
import { getWidgetSettings } from "../../serivces/widgetService";

function AccessibilityProfile({
  color,
  setSelectedTab,
  setSelectedCategory,
  setHiglightId,
}) {
  const [profileData, setProfileData] = useState([]);
  const [showInnerPage, setShowInnerPage] = useState(false);
  const [selectedIdForView, setSelectedForView] = useState(null);
  const [widgetSettings, setWidgetSettings] = useState(null);
  const isResetOpen = useSelector(selectIsResetOpen);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const innerPageTopRef = useRef(null);

  const runAction = (action) => {
    if (typeof action === "function") {
      action();
    }
  };

  const normalizeFeatureKey = (str) => {
    return String(str || "")
      .replace(/\s+/g, "")
      .toLowerCase();
  };

  const syncSpecialState = (item, isSelected) => {
    if (!item) return;

    if (item.title === "Dictionary") {
      dispatch(setDictionaryActive(isSelected));
    } else if (item.title === "Voice Navigation") {
      dispatch(setVoiceNavigationActive(isSelected));
    } else if (item.title === "Page Structure") {
      dispatch(setPageStructureActive(isSelected));
    } else if (item.title === "Tooltips") {
      if (isSelected) {
        setTooltipStatus(true);
      } else {
        removeTooltipStatus();
      }
    }
  };

  const activateProfileChildren = (profiles, parentTitle) => {
    for (let i = 0; i < profiles.length; i++) {
      const child = profiles[i];

      if (
        child.profile !== true &&
        child.subItemOf.includes(parentTitle) &&
        (!isMobile() || child.isMobileActive) &&
        getFunctionValue(normalizeFeatureKey(child.title))
      ) {
        if (child.totalTapCount > 0) {
          child.currentCount = 1;
          child.isSelected = true;
          runAction(child.items?.[0]?.subItemAction);
        } else {
          child.currentCount = 0;
          child.isSelected = true;
          runAction(child.action);
        }

        syncSpecialState(child, true);
      }
    }
  };

  const deactivateProfileChildren = (profiles, parentTitle) => {
    for (let i = 0; i < profiles.length; i++) {
      const child = profiles[i];

      if (child.profile !== true && child.subItemOf.includes(parentTitle)) {
        child.isSelected = false;
        child.currentCount = 0;
        runAction(child.unSelectedAction);
        syncSpecialState(child, false);
      }
    }
  };

  const toggleSwitch = (id) => {
    const updatedProfiles = [...profileData];
    let selectedData = null;
    let parentSelectedState = false;
    let lastSelectedProfile = null;

    for (let i = 0; i < updatedProfiles.length; i++) {
      const profile = updatedProfiles[i];

      if (profile.profile === true && profile.customize === false) {
        if (profile.id === id) {
          selectedData = profile;
          profile.isSelected = !profile.isSelected;
          parentSelectedState = profile.isSelected;
          setSelectedForView(profile);
        } else {
          if (profile.isSelected) {
            lastSelectedProfile = profile;
          }
          profile.isSelected = false;
        }
      }
    }

    if (lastSelectedProfile) {
      runAction(lastSelectedProfile.unSelectedAction);
      deactivateProfileChildren(updatedProfiles, lastSelectedProfile.title);
    }

    if (selectedData) {
      if (parentSelectedState) {
        runAction(selectedData.action);
        activateProfileChildren(updatedProfiles, selectedData.title);
      } else {
        runAction(selectedData.unSelectedAction);
        deactivateProfileChildren(updatedProfiles, selectedData.title);
      }
    }

    setProfileList(updatedProfiles);
    setProfileData(updatedProfiles);
    dispatch(setProfileDataRedux(updatedProfiles));
  };

  const handleInnerPage = (id) => {
    setShowInnerPage(!showInnerPage);
    for (let i = 0; i < profileData.length; i++) {
      if (profileData[i].id === id) {
        setSelectedForView(profileData[i]);
      }
    }
  };

  useEffect(() => {
    if (showInnerPage && innerPageTopRef.current) {
      innerPageTopRef.current.style.scrollMarginTop = "70px";
      innerPageTopRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showInnerPage, selectedIdForView]);

  useEffect(() => {
    const storedData = getProfileList();

    if (storedData) {
      setProfileData(storedData);

      for (let i = 0; i < storedData.length; i++) {
        if (storedData[i].isSelected === true) {
          if (storedData[i].totalTapCount > 0) {
            runAction(
              storedData[i].items?.[storedData[i].currentCount - 1]
                ?.subItemAction,
            );
          } else {
            runAction(storedData[i].action);
          }
        }
      }
    } else {
      setProfileData(data.profile);
    }

    getWidget();
  }, []);

  useEffect(() => {
    if (isResetOpen) {
      const updatedProfiles = [...profileData];

      for (let i = 0; i < updatedProfiles.length; i++) {
        updatedProfiles[i].isSelected = false;
        updatedProfiles[i].currentCount = 0;
        runAction(updatedProfiles[i].unSelectedAction);
        syncSpecialState(updatedProfiles[i], false);
      }

      setProfileData(updatedProfiles);
      setProfileList(updatedProfiles);
      dispatch(setProfileDataRedux(updatedProfiles));

      localStorage.setItem(
        "colorSliderValues",
        JSON.stringify({ textColor: "", backgroundColor: "" }),
      );
    }
  }, [isResetOpen]);

  useEffect(() => {
    if (color) {
      document.documentElement.style.setProperty("--toggle-active-bg", color);
    }
  }, [color]);

  const getWidget = async () => {
    const resp = await getWidgetSettings();
    setWidgetSettings(resp);
  };

  const getFunctionValue = (key) => {
    return widgetSettings?.advanced?.functions?.[key];
  };

  useKeyBoardShortcut(profileData, setProfileData, setProfileList);
  usePageStructureSync(profileData, setProfileData, setProfileList);
  useVoiceNavSync(profileData, setProfileData, setProfileList);

  return (
    <>
      {!showInnerPage && (
        <>
          {profileData.map((item, index) => (
            <React.Fragment key={index}>
              {item.isVisible && item.profile && (
                <div className="w-[100%] flex items-center justify-between profileContainer pt-[0.88rem] pb-[0.88rem] pl-[0.88rem] border-b-2">
                  <div className="flex items-center justify-center gap-[1.05rem]">
                    <div className="w-[1.75rem] h-[1.75rem] shrink-0 aspect-square">
                      <div
                        style={{
                          backgroundImage: `url(${item.iconUnselected})`,
                        }}
                        className="w-[100%] h-[100%] bg-no-repeat bg-center bg-contain"
                      />
                    </div>
                    <div className="flex flex-col items-start justify-center">
                      <div className="text-nowrap text-secondary font-[600] leading-[1.375rem] text-text-black profileTitle">
                        {t(item.title)}
                      </div>
                      <div className="text-nowrap text-primary font-[400] leading-[1.125rem] text-text-black profileSubTitle text-left">
                        {t(item.subtitle)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-[0.88rem]">
                    <label
                      className="toggle-container rounded-[5rem] focus:outline-none focus:ring-2 focus:#3B4395"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleSwitch(item.id);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={item.isSelected}
                        onChange={() => toggleSwitch(item.id)}
                        className="sr-only"
                      />
                      <div
                        className={`toggle-switch ${
                          item.isSelected ? "on" : ""
                        }`}
                        aria-checked={item.isSelected}
                        role="switch"
                      ></div>
                    </label>

                    <button
                      className="w-[0.75rem] h-[1.5rem] cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-[50%]"
                      onClick={() => handleInnerPage(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleInnerPage(item.id);
                        }
                      }}
                    >
                      <div
                        style={{
                          backgroundImage: `url('/widgetIcon/leftArrowProfile.svg')`,
                        }}
                        className="w-full h-full cursor-pointer bg-no-repeat bg-center bg-contain"
                      />
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </>
      )}

      {showInnerPage && selectedIdForView && (
        <React.Fragment>
          <div ref={innerPageTopRef}>
            {profileData.map((item, index) => (
              <React.Fragment key={`top-${index}`}>
                {item.id === selectedIdForView.id && (
                  <div className="flex items-center justify-between mt-[1.25rem]">
                    <button
                      className="w-[1.75rem] h-[1.75rem] shrink-0 aspect-square cursor-pointer focus:outline-none focus:ring-2 focus:#3B4395 rounded-[50%]"
                      onClick={() => handleInnerPage(item.id)}
                    >
                      <div
                        style={{
                          backgroundImage: `url(/widgetIcon/backArrow.svg)`,
                        }}
                        className="w-[100%] h-[100%] cursor-pointer bg-no-repeat bg-center bg-contain"
                      />
                    </button>

                    <div className="flex items-center justify-center gap-[0.88rem]">
                      <div
                        style={{
                          backgroundImage: `url(${item.iconUnselected})`,
                        }}
                        className="w-[1.75rem] h-[1.75rem] cursor-pointer bg-no-repeat bg-center bg-contain"
                      />
                      <div className="text-secondary font-[600] leading-[1.375rem] text-text-black">
                        {t(item.title)}
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-[0.88rem]">
                      <label
                        className="toggle-container rounded-[5rem] focus:outline-none focus:ring-2 focus:#3B4395"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleSwitch(item.id);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isSelected}
                          onChange={() => toggleSwitch(item.id)}
                          className="sr-only"
                        />
                        <div
                          className={`toggle-switch ${
                            item.isSelected ? "on" : ""
                          }`}
                          aria-checked={item.isSelected}
                          role="switch"
                        ></div>
                      </label>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-[1.94rem]">
            {(selectedIdForView.id === 4 || selectedIdForView.id === 5) && (
              <OuterRangeSlider
                color={color}
                key={0}
                data={data.customizeFont[0].fontSize[0]}
              />
            )}
          </div>

          <div
            className={`flex items-center mt-[1.88rem] flex-wrap ${
              isMobile()
                ? "gap-[1.25rem] justify-center"
                : "gap-[0.7rem] justify-start"
            }`}
          >
            {(() => {
              let renderedCount = 0;

              return (
                <>
                  {profileData.map((item, index) => {
                    const shouldRender =
                      item.profile === false &&
                      item.customize === true &&
                      item.subItemOf.includes(selectedIdForView.title) &&
                      getFunctionValue(normalizeFeatureKey(item.title));

                    if (!shouldRender) return null;

                    renderedCount++;

                    return (
                      <div
                        key={`inner-${index}`}
                        onClick={() => {
                          setSelectedTab("Customize");
                          setSelectedCategory(item.type);
                          setHiglightId(item.id);
                        }}
                      >
                        <DisplayCard
                          color={color}
                          items={item}
                          selectedData={selectedIdForView}
                        />
                      </div>
                    );
                  })}

                  {isMobile() && renderedCount % 2 !== 0 && (
                    <div className="displayCardUnSelectedEmpty"></div>
                  )}

                  {renderedCount === 0 && (
                    <div className="text-primary font-[500] leading-[1.375rem] text-[#6F6F76] flex items-center justify-center text-center m-auto">
                      {t("Please activate a profile to view its settings.")}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </React.Fragment>
      )}
    </>
  );
}

export default AccessibilityProfile;
